import { useLocalization } from "@fluent/react";
import { useFormState } from "react-hook-form";
import { DateInput } from "@lending/components/DateInput/DateInput";
import { TaxIDInput } from "@lending/components/TaxIDInput/TaxIDInput";
import { TextInput } from "@lending/components/TextInput/TextInput";
import styles from "./PersonalInfoEditForm.module.css";
import type { Control, FieldValues } from "react-hook-form";

interface PersonalInfoReviewFormData extends FieldValues {
  first_name: string;
  last_name: string;
  tax_id: string;
  date_of_birth: string;
  street: string;
  street2: string;
  city: string;
  region: string;
  postal_code: string;
}

interface PersonalInfoEditFormProps {
  control: Control<PersonalInfoReviewFormData>;
  isInitiallyMasked: boolean;
}

function SectionIcon({ missing }: { missing: boolean }) {
  if (missing) {
    return <span className={`narmi-icon-alert-circle ${styles.sectionIconError}`} />;
  }
  return <span className={`narmi-icon-check-circle ${styles.sectionIconSuccess}`} />;
}

function PersonalInfoEditForm({ control, isInitiallyMasked }: PersonalInfoEditFormProps) {
  const { l10n } = useLocalization();
  const { errors } = useFormState({ control });

  const personalDetailsMissing =
    !!errors.first_name || !!errors.last_name || !!errors.tax_id || !!errors.date_of_birth;
  const addressMissing =
    !!errors.street || !!errors.city || !!errors.region || !!errors.postal_code;

  return (
    <>
      <div className={styles.sectionLabel}>
        <SectionIcon missing={personalDetailsMissing} />
        {l10n.getString("plaid-pii-edit-personal-details-section", null, "Personal details")}
      </div>
      <div className={styles.personalGrid}>
        <TextInput
          control={control}
          name="first_name"
          label={l10n.getString("form-input-first-name-label", null, "First name")}
        />
        <TextInput
          control={control}
          name="last_name"
          label={l10n.getString("form-input-last-name-label", null, "Last name")}
        />
        <TaxIDInput
          control={control}
          name="tax_id"
          label={l10n.getString("form-input-ssn-label", null, "Social security number")}
          isInitiallyMasked={isInitiallyMasked}
        />
        <DateInput
          control={control}
          name="date_of_birth"
          label={l10n.getString("form-input-date-of-birth-label", null, "Date of birth")}
        />
      </div>
      <div className={styles.sectionLabel}>
        <SectionIcon missing={addressMissing} />
        {l10n.getString("plaid-pii-edit-address-section", null, "Address")}
      </div>
      <div className={styles.addressGrid}>
        <TextInput
          control={control}
          name="street"
          label={l10n.getString("form-input-street-label", null, "Street")}
        />
        <TextInput
          control={control}
          name="street2"
          label={l10n.getString("form-input-apt-suite-label", null, "Apt/Suite")}
        />
        <TextInput
          control={control}
          name="city"
          label={l10n.getString("form-input-city-label", null, "City")}
        />
        <TextInput
          control={control}
          name="region"
          label={l10n.getString("form-input-state-label", null, "State")}
        />
        <TextInput
          control={control}
          name="postal_code"
          label={l10n.getString("form-input-zip-code-label", null, "ZIP code")}
        />
      </div>
    </>
  );
}

export type { PersonalInfoReviewFormData };
export { PersonalInfoEditForm };
